import { useState } from "react";
import ExtensionMainModal from "./MainModal";
import DenyModal from "./DenyModal";
import { STEPS } from "./types";
import ApproveModal from "./ApproveModal";
import SendToLeadModal from "./SendToLeadModal";
import { useUpdateExtensionRequest } from "@/queries/extension";
import type { ApprovalGroupType } from "@/types/entities/approval-groups";
import type { ExtensionRequestType } from "@/types/entities/extension";
import { useToast } from "@elaniintc/goes-design-system";

interface Props {
  id: string;
  isOpen: boolean;
  canSendToLead?: boolean;
  onClose: () => void;
  group?: ApprovalGroupType;
  data: ExtensionRequestType;
  canAction?: boolean;
}

const ExtensionModal = ({
  id,
  isOpen,
  canSendToLead,
  data,
  group,
  canAction,
  onClose,
}: Props) => {
  const { showToast } = useToast();
  const { mutateAsync: updateRequest, isPending } =
    useUpdateExtensionRequest(id);
  const [step, setStep] = useState<STEPS>(STEPS.MAIN);

  // const data = {
  //   user: {
  //     initial: "S",
  //     fullName: "Nombre y apellido",
  //     email: "username@goes.gob.sv",
  //   },
  //   step: {
  //     name: "Nombre del pago",
  //     time: "3d 24h 59m",
  //   },
  //   comment: "Tempora ex ",
  //   file: {
  //     name: "File name.pdf",
  //     size: "15MB",
  //     url: "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf",
  //   },
  // };

  const onApprove = async () => {
    try {
      await updateRequest({ action: "APPROVE" });
      onClose();
    } catch {
      showToast({
        title: "Error al aprobar la extensión",
        description:
          "Ha ocurrido un error al aprobar la extensión. Por favor, intenta nuevamente.",
        options: {
          type: "error",
        },
      });
    }
  };

  const onDeny = async (reason: string) => {
    try {
      await updateRequest({ action: "DENY", comment: reason });
      onClose();
    } catch {
      showToast({
        title: "Error al denegar la extensión",
        description:
          "Ha ocurrido un error al denegar la extensión. Por favor, intenta nuevamente.",
        options: {
          type: "error",
        },
      });
    }
  };

  const onSendToLead = async (reason: string) => {
    const leader = group?.members.find((member) => {
      return member.seniority === 1;
    });
    try {
      await updateRequest({
        action: "SCALE",
        comment: reason,
        scaled_to: leader?.id,
      });

      onClose();
    } catch {
      showToast({
        title: "Error al enviar al líder",
        description:
          "Ha ocurrido un error al enviar la extensión al líder. Por favor, intenta nuevamente.",
        options: {
          type: "error",
        },
      });
    }
  };

  return (
    <>
      <ExtensionMainModal
        isOpen={isOpen && step === STEPS.MAIN}
        onClose={onClose}
        onApprove={() => setStep(STEPS.APPROVE)}
        onDenied={() => setStep(STEPS.DENY)}
        onSendToLead={() => setStep(STEPS.SEND_TO_LEAD)}
        data={data}
        canSendToLead={canSendToLead}
        canAction={canAction}
      />
      <ApproveModal
        data={data}
        isOpen={isOpen && step === STEPS.APPROVE}
        onClose={() => setStep(STEPS.MAIN)}
        onApprove={onApprove}
        acceptButtonDetails={{
          label: "Aprobar",
          variant: undefined,
        }}
        isLoading={isPending}
      />
      <DenyModal
        isOpen={isOpen && step === STEPS.DENY}
        onClose={() => setStep(STEPS.MAIN)}
        onDeny={onDeny}
        acceptButtonDetails={{
          label: "Denegar",
          variant: "danger-primary",
        }}
        isLoading={isPending}
      />
      <SendToLeadModal
        isOpen={isOpen && step === STEPS.SEND_TO_LEAD}
        onClose={() => setStep(STEPS.MAIN)}
        onSendToLead={onSendToLead}
        acceptButtonDetails={{
          label: "Enviar al líder",
          variant: undefined,
        }}
        isLoading={isPending}
      />
    </>
  );
};

export default ExtensionModal;
